import { FC, ReactNode } from "react";
import { ChecksumAddress } from "@silo/types";
import { usePositionsPanelContext } from "../position-panel-context";
import { AmountInUSD } from "./amount-in-usd";
import { LiquidationPriceState } from "../utils";
import { match } from "ts-pattern";
import { PointsInfo } from "../types";
import { asDecimal, asFullUSD } from "@silo/utils";
import { AssetAddressContext } from "../../asset-address-context";
import { KeyValueList } from "../../key-value-list";
import { EmptyText } from "../../empty-text";

interface LoanAssetProps {
  assetAddress: ChecksumAddress;
  symbol: string;
  decimals: number;
  balance: bigint;
  balanceType: ReactNode;
  balanceLabel: ReactNode;
  aprSlot: ReactNode;
  points: PointsInfo[];
  liquidationPriceState: LiquidationPriceState;
  liquidationPriceDescription?: ReactNode;
  divider?: boolean;
}

export const LoanAsset: FC<LoanAssetProps> = ({
  assetAddress,
  symbol,
  balanceType,
  balanceLabel,
  balance,
  decimals,
  aprSlot,
  points,
  liquidationPriceState,
  liquidationPriceDescription,
  divider = false,
}) => {
  const { assetLogo } = usePositionsPanelContext();
  const showLiquidationPrice = liquidationPriceState._tag !== "none";

  return (
    <div className="flex flex-col gap-5 @[1350px]:flex-row @[1350px]:items-start @[1350px]:gap-10">
      <div className="flex gap-2.5 flex-wrap @[1350px]:flex-[2]">
        <div className="flex items-center gap-2 rounded-lg bg-shale-750 px-5 py-2.5">
          <div>
            <AssetAddressContext.Provider value={assetAddress}>
              {assetLogo}
            </AssetAddressContext.Provider>
          </div>
          <div data-cy="symbol" className="font-medium text-white">
            {symbol}
          </div>
        </div>

        <div className="rounded-lg bg-shale-750 px-5 py-2.5 font-medium self-stretch flex flex-col justify-center">
          <div>{balanceType}</div>
        </div>
      </div>

      <KeyValueList divider={divider} className="@[1350px]:flex-[3]">
        <KeyValueList.Pair>
          <KeyValueList.Key>{balanceLabel}</KeyValueList.Key>
          <KeyValueList.Value>
            <span data-cy="balance">
              {asDecimal({ value: balance, decimals })}
            </span>
            <span
              className="text-sm font-normal text-shale-200"
              data-cy="usd-balance"
            >
              {
                <AmountInUSD
                  assetAddress={assetAddress}
                  decimals={decimals}
                  amount={balance}
                />
              }
            </span>
          </KeyValueList.Value>
        </KeyValueList.Pair>

        {aprSlot}

        {points.length > 0 && (
          <KeyValueList.Pair>
            <KeyValueList.Key>Points</KeyValueList.Key>

            <KeyValueList.Value className="font-sans">
              {points.map(({ label }) => (
                <div>{label}</div>
              ))}
            </KeyValueList.Value>
          </KeyValueList.Pair>
        )}

        {showLiquidationPrice && (
          <KeyValueList.Pair description={liquidationPriceDescription}>
            <KeyValueList.Key>Liquidation price</KeyValueList.Key>
            <KeyValueList.Value>
              {match(liquidationPriceState)
                .with({ _tag: "notApplicable" }, () => "--")
                .with({ _tag: "loading" }, () => <EmptyText />)
                .with({ _tag: "loaded" }, ({ value }) => asFullUSD(value))
                .exhaustive()}
            </KeyValueList.Value>
          </KeyValueList.Pair>
        )}
      </KeyValueList>
    </div>
  );
};
